// src/components/wellfinder/WellCard.jsx
//
// Unified well detail card.
// Shows detected well data  linked RRC record + spatial offset delta.
// Used inside AIPrime WellFinderPanel when a well is selected.

import { MapPin, Crosshair, ArrowUp, AlertTriangle, PanelRight, ShieldAlert } from "lucide-react";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function confidenceBar(value) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color =
    pct >= 70 ? "#22c55e" :
      pct >= 40 ? "#f59e0b" :
        "#ef4444";
  return { pct, color };
}

function MetaRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex justify-between items-baseline gap-2 py-1 border-b border-slate-800/60 last:border-0">
      <span className="text-[10px] text-fg-secondary shrink-0">{label}</span>
      <span className="text-[10px] text-gray-300 text-right truncate max-w-[180px]">{value}</span>
    </div>
  );
}

function SectionHeader({ icon: Icon, label, color = "text-gray-400" }) {
  return (
    <div className={`flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider ${color} mb-2`}>
      <Icon size={11} />
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Match status badge + offset display
// ---------------------------------------------------------------------------

function MatchStatusBadge({ status }) {
  const cfg = {
    strong: { label: "Strong Match", color: "text-green-400", bg: "bg-green-400/10", border: "border-green-400/20" },
    candidate: { label: "Candidate Match", color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20" },
    unverified: { label: "Unverified", color: "text-red-400", bg: "bg-red-400/10", border: "border-red-400/20" },
    // qc_conflict was missing, so it fell through to the default below and
    // rendered the raw token "qc_conflict" in grey — the weakest treatment in
    // the set, for the state that most needs attention. It is orange to match
    // STATUS_COLORS.qc_conflict on the map, and distinct from unverified's red:
    // unverified means "withdrawn, awaiting re-review", qc_conflict means
    // "the registry and the imagery genuinely disagree". Different facts.
    qc_conflict: { label: "QC Conflict", color: "text-orange-400", bg: "bg-orange-400/10", border: "border-orange-400/20" },
    pending: { label: "Pending Match", color: "text-gray-400", bg: "bg-gray-400/10", border: "border-gray-400/20" },
  }[status] ?? { label: status, color: "text-gray-400", bg: "bg-gray-400/10", border: "border-gray-400/20" };

  return (
    <span className={`
      inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium
      border ${cfg.bg} ${cfg.border} ${cfg.color}
    `}>
      {cfg.label}
    </span>
  );
}

const BEARING_ROTATION = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
  E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
  W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
};

function OffsetDisplay({ link, detectedProps }) {
  if (!link) return null;

  const { offset_m, offset_ft, match_confidence } = link;

  // offset_bearing — prefer link column, fall back to detected props
  const offset_bearing = link.offset_bearing ?? detectedProps?.offset_bearing ?? null;

  // 'unverified' is explicit rather than falling into the grey default. Grey is
  // also what "pending" and any unrecognised value get, so without this a match
  // an analyst has withdrawn vouching for looked exactly like one that has no
  // state yet. Red matches MatchStatusBadge, which already labels it Unverified.
  const color =
    match_confidence === "strong" ? "text-green-400" :
      match_confidence === "candidate" ? "text-amber-400" :
        match_confidence === "unverified" ? "text-red-400" :
          match_confidence === "qc_conflict" ? "text-orange-400" :
            "text-gray-400";

  const rotation = BEARING_ROTATION[offset_bearing] ?? 0;

  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-md
                    bg-slate-800/60 border border-slate-700/60 mt-2">
      <div className="flex items-center gap-2">
        <div style={{ transform: `rotate(${rotation}deg)` }} className="transition-transform duration-300">
          <ArrowUp size={13} className={color} />
        </div>
        <span className={`text-[11px] font-medium ${color}`}>
          {offset_ft != null ? `${offset_ft} ft` : `${offset_m?.toFixed(1)} m`}
        </span>
        {/* offset_bearing is measured FROM the detection TO the RRC well
            (ST_Azimuth(detection, rrc) in adjudication.js and the assignment
            job; gdb.js OFFSET_BRG says the same). So the subject is the RRC
            well. The old wording, "X of RRC location", made the detection the
            subject and read 180° off the map; the arrow was always right. */}
        {offset_bearing && (
          <span className="text-[10px] text-fg-secondary">
            RRC well is {offset_bearing} of detection
          </span>
        )}
      </div>
      <MatchStatusBadge status={match_confidence} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linked RRC panel notice
// ---------------------------------------------------------------------------

function LinkedPanelNotice({ rrcAssetId }) {
  if (!rrcAssetId) return null;
  return (
    <div className="mx-4 mt-2 mb-1 px-3 py-2 rounded-md
                    bg-orange-400/8 border border-orange-400/20
                    flex items-center gap-2">
      <PanelRight size={11} className="text-orange-400 shrink-0" />
      <span className="text-[10px] text-orange-300 leading-relaxed">
        Full RRC data open in right panel
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// No-well banner — shown when qc_type is "no_well"
// ---------------------------------------------------------------------------

function NoWellBanner() {
  return (
    <div className="mx-4 mt-3 px-3 py-2 rounded-md bg-slate-700/40 border border-slate-600/60
                    flex items-start gap-2">
      <ShieldAlert size={13} className="text-slate-400 mt-0.5 shrink-0" />
      <div>
        <div className="text-[11px] font-medium text-slate-300">QC Cleared — No Well</div>
        <div className="text-[10px] text-fg-secondary mt-0.5 leading-relaxed">
          This location was reviewed and confirmed to have no wellhead.
          It is retained as a negative QC record.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detected section
// ---------------------------------------------------------------------------

const QC_TYPE_LABELS = {
  pump_jack: "Pump Jack",
  frac_well: "Frac Well",
  no_well: "No Well (QC Cleared)",
  tank: "Tank",
  other: "Other",
};

// The adjudication verdict, for the two rows that report it. 'approved' has no
// entry: an approved record shows WHO approved it, which is more use than the
// word "approved" next to a name-shaped gap.
const REVIEW_STATUS_LABELS = {
  pending:      "Not reviewed",
  needs_review: "Needs review",
  rejected:     "Rejected",
};

const RELIABILITY_LABELS = {
  "10": "Very Low",
  "20": "Low",
  "30": "Medium",
  "40": "High",
  "55": "Very High",
};

function DetectedSection({ detected }) {
  if (!detected) return null;
  const conf = confidenceBar(detected.confidence);
  const props = detected.props ?? {};

  const qcType = props.qc_type;
  const reliability = props.reliability ? RELIABILITY_LABELS[String(props.reliability)] ?? props.reliability : null;

  return (
    <div className="px-4 py-3 border-b border-slate-800">
      <SectionHeader icon={Crosshair} label="Detected Location" color="text-accent-info" />
      <MetaRow label="Detection ID" value={detected.detection_id} />
      <MetaRow label="Model" value={detected.model_key} />
      <MetaRow label="Source" value={detected.source} />
      <MetaRow label="Detected" value={
        detected.detected_at
          ? new Date(detected.detected_at).toLocaleDateString()
          : null
      } />
      <MetaRow label="Coordinates" value={
        detected.lng != null
          ? `${detected.lat?.toFixed(6)}, ${detected.lng?.toFixed(6)}`
          : null
      } />
      <MetaRow label="Detection Type" value={QC_TYPE_LABELS[qcType] ?? qcType} />

      {/* ── REVIEW OUTPUT, NOT DETECTION OUTPUT ──────────────────────────────
          Equipment is what the ANALYST identified; the model emits none at all,
          so these three describe the review rather than the detection. They sit
          under the detected heading because that is where an analyst looks for
          "what is this and has anyone checked it", which is one question.

          All three are null on a pending record — 6,826 of 10,800 — and MetaRow
          renders nothing for a null, so an unreviewed well's card is exactly as
          short as it was before. */}
      <MetaRow
        label="Equip Type"
        value={QC_TYPE_LABELS[detected.equipment_type] ?? detected.equipment_type}
      />
      {/* THE ?? "Approved" FALLBACK IS LOAD-BEARING, not cosmetic. The API
          sends reviewed_by_name ONLY when the reviewer shares an active org
          with the caller — a customer looking at their own wells gets null and
          lands here. Do not "fix" the blank by reaching for an email or a user
          id: that is the disclosure the server-side gate exists to prevent, and
          the card is the wrong place to re-decide it. See wells.mjs. */}
      <MetaRow
        label="QA Approved"
        value={
          detected.review_status === "approved"
            ? (detected.reviewed_by_name ?? "Approved")
            : (REVIEW_STATUS_LABELS[detected.review_status] ?? null)
        }
      />
      {/* Gated on APPROVED, not merely on reviewed_at being set. reviewed_at is
          stamped by every verdict, so an unguarded row renders a REJECTION date
          under a label that says "Approved" — six records today would say it.
          The QA row above already reports "Rejected"; this row stays out. */}
      <MetaRow
        label="Date Approved"
        value={
          detected.review_status === "approved" && detected.reviewed_at
            ? new Date(detected.reviewed_at).toLocaleDateString()
            : null
        }
      />

      {/* qa_status and reliability were props on the vector_assets_org row and
          have NO successor on the observation plane — both have rendered
          nothing since 062. Kept, not deleted: the fields may return with a
          model that emits them, and MetaRow already hides them meanwhile. The
          QA row above is the real one. */}
      <MetaRow label="Reliability" value={reliability} />

      {conf && (
        <div className="mt-2">
          <div className="flex justify-between text-[10px] mb-1">
            <span className="text-fg-secondary">Detection Confidence</span>
            <span style={{ color: conf.color }}>{conf.pct}%</span>
          </div>
          <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${conf.pct}%`, backgroundColor: conf.color }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RRC section
// ---------------------------------------------------------------------------

function RrcSection({ rrc, hasLinkedPanel }) {
  if (!rrc) return (
    <div className="px-4 py-3 border-b border-slate-800">
      <SectionHeader icon={MapPin} label="RRC Record" color="text-orange-400" />
      <div className="text-[10px] text-fg-secondary italic">
        No RRC record linked to this detection.
      </div>
    </div>
  );

  const statusColor = {
    active: "text-green-400",
    drilling: "text-blue-400",
    shut_in: "text-amber-400",
    shutin: "text-amber-400",
    permitted: "text-purple-400",
    unknown: "text-gray-400",
  }[rrc.status_norm] ?? "text-gray-400";

  return (
    <div className="px-4 py-3 border-b border-slate-800">
      <SectionHeader icon={MapPin} label="RRC Record" color="text-orange-400" />

      {rrc.status_norm && (
        <div className="mb-2">
          <span className={`
            text-[10px] px-2 py-0.5 rounded-full
            bg-slate-700 border border-slate-600
            ${statusColor}
          `}>
            {rrc.status_norm.replace("_", " ").toUpperCase()}
          </span>
        </div>
      )}

      <MetaRow label="API #" value={rrc.api} />
      <MetaRow label="Well Name" value={rrc.well_name || rrc.name} />
      <MetaRow label="Operator" value={rrc.operator} />
      <MetaRow label="County" value={rrc.county} />
      <MetaRow label="Field" value={rrc.field} />
      <MetaRow label="Lease" value={rrc.lease} />
      <MetaRow label="Well Type" value={rrc.well_type} />

      {hasLinkedPanel && (
        <div className="mt-2 text-[10px] text-fg-secondary italic">
          Production, compliance & completion data in right panel →
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unverified banner
// ---------------------------------------------------------------------------

// WHICH "NO MATCH" THIS IS (DB 2026-10-25). had_rrc_candidates and
// candidate_radius_m come from GET /wells/:id/detail. The radius is the
// search's own constant, so "within N m" cannot drift from it; it said "50m"
// against a 100 m search. Absent (the RRC-id branch), the banner names neither.
function unverifiedCopy(detected) {
  if (detected?.had_rrc_candidates === false) {
    return {
      title: detected.candidate_radius_m != null
        ? `No RRC well record within ${detected.candidate_radius_m} m`
        : "No RRC well record nearby",
      detail: "This may indicate an unreported well or a data gap.",
    };
  }
  if (detected?.had_rrc_candidates === true) {
    return {
      title: "No matching RRC record",
      detail: "RRC wells are nearby, and a reviewer found that none of them is this well.",
    };
  }
  return { title: "No RRC Match Found", detail: "This may indicate an unreported well or a data gap." };
}

function UnverifiedBanner({ detected }) {
  const copy = unverifiedCopy(detected);
  return (
    <div className="mx-4 mt-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20
                    flex items-start gap-2">
      <AlertTriangle size={13} className="text-red-400 mt-0.5 shrink-0" />
      <div>
        <div className="text-[11px] font-medium text-red-400">{copy.title}</div>
        <div className="text-[10px] text-gray-400 mt-0.5 leading-relaxed">{copy.detail}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QC Conflict Banner
// ---------------------------------------------------------------------------
function QcConflictBanner({ link }) {
  return (
    <div className="mx-4 mt-3 px-3 py-2 rounded-md bg-orange-500/10 border border-orange-500/20
                     flex items-start gap-2">
      <AlertTriangle size={13} className="text-orange-400 mt-0.5 shrink-0" />
      <div>
        <div className="text-[11px] font-medium text-orange-400">QC Conflict — Review Required</div>
        <div className="text-[10px] text-gray-400 mt-0.5 leading-relaxed">
          This location was QC-cleared as no-well, but RRC record{" "}
          {link?.rrc_source_id ? `API ${link.rrc_source_id}` : ""} was found{" "}
          {link?.offset_ft ? `${link.offset_ft} ft away` : "nearby"}.
          Manual review recommended.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card header
// ---------------------------------------------------------------------------

function CardHeader({ detail }) {
  const props = detail.detected?.props ?? {};
  const qcType = props.qc_type;
  const isNoWell = qcType === "no_well";
  const isQcConflict = detail.detected?.match_status === "qc_conflict";

  const name =
    detail.rrc?.well_name ||
    detail.rrc?.name ||
    detail.detected?.detection_id ||
    "Unknown Well";

  const api = detail.rrc?.api || detail.link?.rrc_source_id;
  const isUnverified = !isNoWell && (detail.detected?.match_status === "unverified" || !detail.rrc);
  const rrcAssetId = detail.link?.rrc_asset_id;

  return (
    <div className="p-4 border-b border-slate-800">
      <div className="font-medium text-white text-sm truncate">{name}</div>

      {api && (
        <div className="text-[10px] text-fg-secondary mt-0.5 font-mono">API {api}</div>
      )}

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {detail.detected && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-info/10 border border-accent-info/20 text-accent-info flex items-center gap-1">
            <Crosshair size={9} />
            AI Detected
          </span>
        )}
        {detail.rrc && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-400/10 border border-orange-400/20 text-orange-400 flex items-center gap-1">
            <MapPin size={9} />
            RRC Record
          </span>
        )}
        {isNoWell && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/60 border border-slate-600/60 text-slate-400 flex items-center gap-1">
            <ShieldAlert size={9} />
            QC Cleared
          </span>
        )}
      </div>

      {/* Offset only shown for non-no_well wells with a link */}
      {!isNoWell && detail.link && (
        <OffsetDisplay link={detail.link} detectedProps={props} />
      )}

      {isQcConflict && (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center gap-1">
          <AlertTriangle size={9} />
          QC Conflict
        </span>
      )}

      {isNoWell && <NoWellBanner />}
      {isUnverified && <UnverifiedBanner detected={detail.detected} />}
      {isQcConflict && <QcConflictBanner link={detail.link} />}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Main WellCard
// ---------------------------------------------------------------------------

export default function WellCard({ detail }) {
  if (!detail) return null;

  const rrcAssetId = detail.link?.rrc_asset_id;

  return (
    <div className="flex flex-col">
      <CardHeader detail={detail} />
      <LinkedPanelNotice rrcAssetId={rrcAssetId} />
      <DetectedSection detected={detail.detected} />
      <RrcSection rrc={detail.rrc} hasLinkedPanel={!!rrcAssetId} />
    </div>
  );
}